import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { getComercialCreated, getComercialClosed } from '../../db/api.js'
import Icon from '../../components/icons.jsx'
import TableSort from './TableSort.jsx'

const money = value => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)
const date = value => value ? new Date(value).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—'

export default function CreatedOpportunities({ query, closed = false }) {
  const title = closed ? 'Negócios ganhos e perdidos' : 'Negócios criados'
  const dateField = closed ? 'data_fechamento' : 'created_at'
  const titleId = closed ? 'cd-closed-title' : 'cd-created-title'

  const [sort, setSort] = useState({ field: dateField, direction: 'desc' })
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(false)
  const dialog = useRef(null)
  const scroll = useRef(null)
  const sentinel = useRef(null)
  const cursor = useRef(0)
  const busy = useRef(false)
  const generation = useRef(0)
  const queryKey = JSON.stringify({ ...query, sort: sort.field, direction: sort.direction })

  const load = useCallback(async (reset = false) => {
    if (busy.current && !reset) return
    const id = generation.current
    busy.current = true
    setLoading(true)
    setError(null)
    try {
      const result = await (closed ? getComercialClosed : getComercialCreated)({ ...JSON.parse(queryKey), offset: reset ? 0 : cursor.current })
      if (id !== generation.current) return
      cursor.current = (reset ? 0 : cursor.current) + result.items.length
      if (!result.items.length) cursor.current = result.total
      setTotal(result.total)
      setRows(current => {
        const known = new Set(reset ? [] : current.map(row => row.id))
        return [...(reset ? [] : current), ...result.items.filter(row => !known.has(row.id))]
      })
    } catch {
      if (id === generation.current) setError(`Não foi possível carregar os ${title.toLowerCase()}.`)
    } finally {
      if (id === generation.current) { busy.current = false; setLoading(false) }
    }
  }, [queryKey, closed, title])

  useEffect(() => {
    if (!expanded) return
    generation.current += 1
    cursor.current = 0
    if (scroll.current) scroll.current.scrollTop = 0
    setRows([])
    setTotal(0)
    load(true)
    return () => { generation.current += 1 }
  }, [load, expanded])

  useEffect(() => {
    if (!expanded) return
    const element = dialog.current
    element.showModal()
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { element.close(); document.body.style.overflow = previous }
  }, [expanded])

  useEffect(() => {
    if (!expanded || loading || error || !sentinel.current || cursor.current >= total) return
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) load()
    }, { root: scroll.current, rootMargin: '60px' })
    observer.observe(sentinel.current)
    return () => observer.disconnect()
  }, [expanded, loading, error, total, rows, load])

  const table = (large = false) => <div className={`cd-table-scroll cd-risk-scroll${large ? ' cd-risk-scroll-expanded' : ''}`} ref={large ? scroll : undefined} onScroll={event => {
    const el = event.currentTarget
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60 && !error && cursor.current < total) load()
  }}>
    <table className="cd-risk-table">
      {large && <colgroup>{[28, 14, 16, 18, 14, 10].map((width, index) => <col key={index} style={{ width: `${width}%` }} />)}</colgroup>}
      <thead><tr>{[['titulo', 'Oportunidade'], ['valor', 'Valor'], ['etapa', 'Etapa'], ['responsavel', 'Responsável'], [dateField, closed ? 'Fechado em' : 'Criado em'], ['status', 'Status']].map(([field, label]) => <th key={field} aria-sort={sort.field === field ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><TableSort field={field} sort={sort} onChange={setSort}>{label}</TableSort></th>)}</tr></thead>
      <tbody>{rows.map(row => <tr key={row.id}>
        <td><Link to={`/comercial/pipelines?deal=${encodeURIComponent(row.id)}`} target="_blank" rel="noopener noreferrer">{row.titulo}</Link></td>
        <td>{money(row.valor)}</td><td>{row.etapa}</td><td>{row.responsavel}</td><td>{date(row[dateField])}</td><td className={row.status === 'ganho' ? 'cd-origin-won' : row.status === 'perdido' ? 'cd-origin-lost' : undefined}>{{ aberto: 'Aberto', ganho: 'Ganho', perdido: 'Perdido' }[row.status] || row.status}</td>
      </tr>)}</tbody>
    </table>
    {!rows.length && !loading && !error && <div className="cd-empty">{closed ? 'Nenhum negócio ganho ou perdido no período.' : 'Nenhum negócio criado no período.'}</div>}
    {(loading || error || cursor.current < total) && <div className="cd-risk-more" aria-live="polite" ref={large ? sentinel : undefined}>
      {loading ? 'Carregando oportunidades…' : <>{error && <span>{error} </span>}<button type="button" onClick={() => load()}>{error ? 'Tentar novamente' : 'Carregar mais 5'}</button></>}
    </div>}
  </div>

  return <>
    <button type="button" className="cd-risk-expand" onClick={() => setExpanded(true)}>{closed ? 'Ver todos os negócios ganhos e perdidos' : 'Ver todos os negócios criados'} <Icon n="arrow_right" s={14} /></button>
    {expanded && createPortal(<dialog ref={dialog} className="cd-risk-dialog cd-created-dialog" aria-labelledby={titleId} onCancel={() => setExpanded(false)} onClose={() => setExpanded(false)}>
      <header><h2 id={titleId}>{title} <span className="cd-count-badge">{total}</span></h2><button type="button" autoFocus aria-label={`Fechar ${title.toLowerCase()}`} onClick={() => setExpanded(false)}>×</button></header>
      {table(true)}
    </dialog>, document.body)}
  </>
}
